import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'
import * as React from 'react'
import { requireCapability } from '@/lib/auth'
import { formatCurrency } from '@/lib/formatters'
import { createFileRoute,Outlet,useNavigate,useSearch } from '@tanstack/react-router'
import { z } from 'zod'
import { useQuery,useMutation,useQueryClient } from '@tanstack/react-query'
import { api,type AxiosError,type MaybeEnveloped,type ListEnvelope } from '@/lib/api-client'
import { useTheme } from 'next-themes'
import { toast } from 'sonner'
import {
Table,
TableBody,
TableCell,
TableHead,
TableHeader,
TableRow,
} from '@/components/ui/table'
import {
DropdownMenu,
DropdownMenuContent,
DropdownMenuItem,
DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
MoreHorizontal,
Plus,
Loader2,
Search,
Trash2,
Pencil,X,CheckCircle,RefreshCw,
Ban,
PlayCircle
} from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
Dialog,
DialogContent,
DialogDescription,
DialogFooter,
DialogHeader,
DialogTitle,
} from '@/components/ui/dialog'
import { DataTablePagination } from '@/components/dashboard/data-table-pagination'
import { PageBreadcrumb } from '@/components/dashboard/page-breadcrumb'
import { Skeleton } from '@/components/ui/skeleton'
import {
Tooltip,
TooltipContent,
TooltipTrigger,
} from '@/components/ui/tooltip'
import {
HoverCard,
HoverCardContent,
HoverCardTrigger,
} from '@/components/ui/hover-card'
import { ImageWithSkeleton } from '@/components/ui/image-with-skeleton'
import { SaleConfigSheet } from '@/features/catalog/sale-config-sheet'
import { SaleConfigListCompact } from '@/features/catalog/sale-config-list-compact'
import { useOrganizationShop } from '@/hooks/use-organization-shop'
import { queryKeys } from '@/lib/query-keys'
import { extractArray } from '@/features/http'
import type { Product,SaleConfig } from '@/types'
import { StrainHoverCard } from '@/features/catalog/components/strain-hover-card'
import { BrandHoverCard } from '@/features/catalog/components/brand-hover-card'
import { DeleteConfirmationDialog } from '@/components/delete-confirmation-dialog'

const ProductSidePanel = React.lazy(() =>
  import('@/features/catalog/product-side-panel').then(m => ({ default: m.ProductSidePanel }))
)

const AddProductModal = React.lazy(() =>
  import('@/features/catalog/add-product-modal').then(m => ({ default: m.AddProductModal }))
)

const EditProductModal = React.lazy(() =>
  import('@/features/catalog/edit-product-modal').then(m => ({ default: m.EditProductModal }))
)

const searchSchema = z.object({
  page: z.number().catch(1),
  limit: z.number().catch(20),
  search: z.string().catch(''),
  openSaleConfig: z.boolean().catch(false),
  saleConfigProductId: z.string().catch(''),
})

export const Route = createFileRoute('/_dashboard/catalog/products')({
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  beforeLoad: requireCapability('MANAGE_CATALOG'),
  component: ProductsLayout,
  validateSearch: searchSchema,
})

let suppressedAutoOpenSignature: string | null = null

function ProductsLayout() {
  return <Outlet />
}

export function ProductsPage({ selectedId }: { selectedId: string | undefined }) {
  const navigate = useNavigate()
  const { page, limit, search: searchQuery, openSaleConfig, saleConfigProductId: searchSaleConfigProductId } = useSearch({ from: Route.id })
  const selectedProductId = selectedId
  const queryClient = useQueryClient()

  const setSelectedProductId = React.useCallback(
    (id: string | null) => {
      if (id) {
        navigate({
          to: '/catalog/products/$selectedItem',
          params: { selectedItem: id },
          search: (prev) => ({ ...prev }),
        })
        return
      }

      navigate({ to: '/catalog/products', search: (prev) => ({ ...prev }) })
    },
    [navigate],
  )

  const { resolvedTheme } = useTheme()
  const { shopId, shops, isLoading: shopLoading } = useOrganizationShop()
  const pageSignature = `${shopId ?? 'no-shop'}:${page}:${limit}:${searchQuery}`
  const [searchInput, setSearchInput] = React.useState(searchQuery)
  const [isRefreshing, setIsRefreshing] = React.useState(false)

  React.useEffect(() => {
    const timer = setTimeout(() => {
      // Only a changed term resets to page 1 — on mount the term is unchanged,
      // so reloads and shared links keep their ?page=.
      navigate({ to: '.', search: (prev) => ((prev.search ?? '') === searchInput ? prev : { ...prev, search: searchInput, page: 1 }), replace: true })
    }, 500)
    return () => clearTimeout(timer)
  }, [searchInput, navigate])

  // Modal State
  const [isAddModalOpen, setIsAddModalOpen] = React.useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false)
  const [editingProduct, setEditingProduct] = React.useState<Product | null>(null)
  const [editModalFocus, setEditModalFocus] = React.useState<'strain' | 'brand' | null>(null)
  const [editingSaleConfigId, setEditingSaleConfigId] = React.useState<string | null>(null)
  const [isAddSaleConfigOpen, setIsAddSaleConfigOpen] = React.useState(false)
  const [saleConfigProductId, setSaleConfigProductId] = React.useState<string | null>(null)
  const [saleConfigProduct, setSaleConfigProduct] = React.useState<Product | null>(null)

  // Consume openSaleConfig search param (survives route remount after product add)
  React.useEffect(() => {
    if (openSaleConfig) {
      if (searchSaleConfigProductId) {
        setSaleConfigProductId(searchSaleConfigProductId)
        setSaleConfigProduct((current) =>
          current?.id === searchSaleConfigProductId ? current : null,
        )
      }
      setIsAddSaleConfigOpen(true)
      navigate({ to: '.', search: (prev) => ({ ...prev, openSaleConfig: undefined, saleConfigProductId: undefined }), replace: true })
    }
  }, [openSaleConfig, searchSaleConfigProductId, navigate])

  // Delete State
  const [deleteId, setDeleteId] = React.useState<string | null>(null)

  // Suspension State
  const [suspendingProduct, setSuspendingProduct] = React.useState<Product | null>(null)
  const [isSuspendDialogOpen, setIsSuspendDialogOpen] = React.useState(false)
  const [suspendReason, setSuspendReason] = React.useState('')
  const [isResumeDialogOpen, setIsResumeDialogOpen] = React.useState(false)
  const [resumeNote, setResumeNote] = React.useState('')

  const productsQuery = useQuery({
    queryKey: [...queryKeys.products(shopId, { page, limit, search: searchQuery })],
    queryFn: async () => {
      const params: Record<string, string | number> = { page, limit }
      if (searchQuery) params.search = searchQuery

      const response = await api.get<ListEnvelope>(`/products`, { params })
      const pData = extractArray<Product>(response)
      const meta = response.meta || { total: pData.length, totalPages: 1 }

      // One bulk request for the whole page instead of a per-product fan-out
      // (was 20 GET /sale-configs/product/:id per page — Sentry N+1).
      let configsByProduct: Record<string, SaleConfig[]> = {}
      if (pData.length > 0) {
        try {
          configsByProduct = await api.get<Record<string, SaleConfig[]>>(
            `/sale-configs/by-products`,
            { params: { productIds: pData.map((prod) => prod.id).join(','), shopId } },
          )
        } catch {
          // Rows render without config badges; the side panel still fetches
          // its own detail on selection.
          configsByProduct = {}
        }
      }
      const enriched = pData.map((product: Product) => ({
        ...product,
        saleConfigs: configsByProduct[product.id] ?? [],
      }))

      return { data: enriched, meta }
    },
    enabled: !!shopId,
  })

  const classificationsQuery = useQuery({
    queryKey: queryKeys.classificationsMap(),
    queryFn: async () => {
      const response = await api.get<{ data?: { id: string; name: string }[] }>(`/classifications?limit=100`)
      const data = Array.isArray(response.data) ? response.data : []
      const map: Record<string, string> = {}
      data.forEach((c: { id: string; name: string }) => {
        map[c.id] = c.name
      })
      return map
    },
  })
  const classificationsMap = classificationsQuery.data || {}

  const selectedProductQuery = useQuery({
    queryKey: queryKeys.productDetail(selectedProductId, shopId),
    queryFn: async () => {
      const response = await api.get<MaybeEnveloped<Product>>(`/products/${selectedProductId}`)
      const product = (response?.data || response) as Product
      
      // Enrich with sale configs if shopId is available
      if (product && shopId) {
        try {
          const configsResponse = await api.get<SaleConfig[] & { data?: SaleConfig[] }>(`/sale-configs/product/${product.id}`, {
            params: { shopId }
          })
          product.saleConfigs = Array.isArray(configsResponse.data) ? configsResponse.data : 
                               Array.isArray(configsResponse) ? configsResponse : []
        } catch (err) {
          console.error(`Failed to fetch configs for product ${product.id}`, err)
          product.saleConfigs = []
        }
      }
      
      return product
    },
    enabled: !!selectedProductId && !!shopId,
  })

  const products = React.useMemo(
    () => productsQuery.data?.data || [],
    [productsQuery.data?.data],
  )
  const totalItems = productsQuery.data?.meta?.total || products.length
  const totalPages = productsQuery.data?.meta?.totalPages || 1
  const loading = productsQuery.isLoading || shopLoading

  const selectedProduct =
    products.find((p) => p.slug === selectedProductId || p.id === selectedProductId) ||
    selectedProductQuery.data ||
    null
  const activeSaleConfigProductId = saleConfigProductId || selectedProduct?.id || ''
  const activeSaleConfigProduct =
    saleConfigProduct?.id === activeSaleConfigProductId
      ? saleConfigProduct
      : selectedProduct?.id === activeSaleConfigProductId
        ? selectedProduct
        : null

  const currentPageHasSelectedProduct = React.useMemo(
    () =>
      !!selectedProductId &&
      products.some((product) => product.slug === selectedProductId || product.id === selectedProductId),
    [products, selectedProductId],
  )

  const closeSelectedProductPanel = React.useCallback(() => {
    suppressedAutoOpenSignature = pageSignature
    setSelectedProductId(null)
  }, [pageSignature, setSelectedProductId])

  React.useEffect(() => {
    if (loading || products.length === 0) return
    if (currentPageHasSelectedProduct) return
    if (!selectedProductId && suppressedAutoOpenSignature === pageSignature) return
    setSelectedProductId(products[0].slug || products[0].id)
  }, [
    currentPageHasSelectedProduct,
    products,
    loading,
    pageSignature,
    selectedProductId,
    setSelectedProductId,
  ])

  React.useEffect(() => {
    return () => {
      window.setTimeout(() => {
        if (!window.location.pathname.startsWith('/catalog/products')) {
          suppressedAutoOpenSignature = null
        }
      }, 0)
    }
  }, [])

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/products/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.products(shopId) })
      toast.success('Product permanently deleted')
    },
    onError: () => {
      toast.error('Failed to delete product')
    },
    onSettled: () => {
      setDeleteId(null)
    },
  })

  // Suspension Mutations
  const suspendMutation = useMutation({
    mutationFn: async () => {
      if (!suspendingProduct) return
      await api.post(`/products/${suspendingProduct.id}/suspend`, {
        reason: suspendReason.trim() || undefined,
      })
    },
    onSuccess: () => {
      toast.success('Product suspended')
      queryClient.invalidateQueries({ queryKey: queryKeys.products(shopId) })
      setIsSuspendDialogOpen(false)
      setSuspendReason('')
      setSuspendingProduct(null)
    },
    onError: (err: unknown) => {
      const axiosErr = err as AxiosError<{ message?: string }>
      toast.error(axiosErr.response?.data?.message || 'Failed to suspend product')
    },
  })

  const resumeMutation = useMutation({
    mutationFn: async () => {
      if (!suspendingProduct) return
      await api.delete(`/products/${suspendingProduct.id}/suspend`, {
        data: resumeNote.trim() ? { note: resumeNote.trim() } : undefined,
      })
    },
    onSuccess: () => {
      toast.success('Product suspension lifted')
      queryClient.invalidateQueries({ queryKey: queryKeys.products(shopId) })
      setIsResumeDialogOpen(false)
      setResumeNote('')
      setSuspendingProduct(null)
    },
    onError: (err: unknown) => {
      const axiosErr = err as AxiosError<{ message?: string }>
      toast.error(axiosErr.response?.data?.message || 'Failed to resume product')
    },
  })


  return (
    <div className="space-y-6 h-full">
      <PageBreadcrumb />

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Products</h2>
          <p className="text-muted-foreground">
            Manage your product catalog and sale configurations.
          </p>
        </div>
        <Button onClick={() => setIsAddModalOpen(true)} className="transition-all hover:scale-105">
          <Plus className="mr-2 h-4 w-4" /> Add Product
        </Button>
      </div>

      {/* Search & Filters */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2 flex-1">
          <div className="relative max-w-sm w-full group">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search products..."
              className="pl-9 pr-8"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {searchInput && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSearchInput('')}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:bg-muted hover:text-foreground transition-all"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Clear search</span>
              </Button>
            )}
          </div>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0"
            title="Refresh"
            disabled={isRefreshing}
            onClick={() => {
              setIsRefreshing(true)
              void productsQuery.refetch()
              setTimeout(() => setIsRefreshing(false), 600)
            }}
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-6 items-start relative min-h-[500px]">
        {/* Table Section */}
        <div
          className={`flex-1 min-w-0 transition-all duration-300 ${!loading && products.length === 0 ? '' : 'border rounded-md squircle bg-card shadow-sm overflow-hidden'} ${selectedProduct ? 'w-full md:w-[calc(100%-340px)] lg:w-[calc(100%-380px)]' : 'w-full'}`}
        >
          <div className="">
            {!loading && products.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 gap-6 text-center">
                <div className="relative w-24 h-24 opacity-10 grayscale shrink-0">
                  <img src="/misc/logo.png" alt="Logo" className="w-full h-full object-contain" />
                </div>
                <div className="flex flex-col gap-4 items-center">
                  <p className="font-medium text-muted-foreground text-lg">No products found</p>
                  <p className="text-sm text-muted-foreground">Products will appear here once added to your catalog.</p>
                </div>
              </div>
            ) : (
            <>
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead className="w-[300px]">Product</TableHead>
                  <>
                    <TableHead
                      className={`hidden md:table-cell ${selectedProduct ? 'md:hidden xl:table-cell' : ''}`}
                    >
                      Brand
                    </TableHead>
                    <TableHead
                      className={`hidden md:table-cell ${selectedProduct ? 'md:hidden xl:table-cell' : ''}`}
                    >
                      Strains
                    </TableHead>
                    <TableHead
                      className={`hidden lg:table-cell ${selectedProduct ? 'lg:hidden 2xl:table-cell' : ''}`}
                    >
                      Price
                    </TableHead>
                  </>
                  <TableHead className="text-right w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Skeleton className="h-10 w-10 rounded-md shrink-0" />
                          <div className="flex flex-col gap-2">
                            <Skeleton className="h-4 w-32" />
                            <Skeleton className="h-3 w-20" />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className={`hidden md:table-cell ${selectedProduct ? 'md:hidden xl:table-cell' : ''}`}>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                      <TableCell className={`hidden md:table-cell ${selectedProduct ? 'md:hidden xl:table-cell' : ''}`}>
                        <Skeleton className="h-4 w-20" />
                      </TableCell>
                      <TableCell className={`hidden lg:table-cell ${selectedProduct ? 'lg:hidden 2xl:table-cell' : ''}`}>
                        <Skeleton className="h-4 w-16" />
                      </TableCell>
                      <TableCell className="text-right">
                        <Skeleton className="h-6 w-8 ml-auto" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  products.map((product: Product) => (
                    <TableRow
                      key={product.id}
                      className={`cursor-pointer transition-colors group ${selectedProduct?.id === product.id ? 'bg-muted/50' : ''}`}
                      onClick={() =>
                        (selectedProductId === (product.slug || product.id)
                          ? closeSelectedProductPanel()
                          : setSelectedProductId(
                              product.slug || product.id,
                            ))
                      }
                    >
                      {/* Product Cell */}
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-md flex items-center justify-center overflow-hidden bg-muted/50 shrink-0">
                            <ImageWithSkeleton
                              src={
                                product.images?.[0]
                                  ? product.images[0]
                                  : resolvedTheme === 'dark'
                                    ? '/placeholder-dark/Place%20Holder%20Dark%204_4.png'
                                    : '/placeholder-light/Place%20Holder%20Light%204_4.png'
                              }
                              alt={product.name}
                              wrapperClassName="h-full w-full"
                              className={`h-full w-full object-cover ${!product.images?.[0] ? 'opacity-50' : ''}`}
                            />
                          </div>
                          <div className="flex flex-col">
                            <span className="font-medium">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="truncate inline-block max-w-[12.5rem] align-middle cursor-help">
                                    {product.name}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{product.name}</p>
                                </TooltipContent>
                              </Tooltip>
                              {product.isSuspended ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-block h-2 w-2 rounded-full bg-red-500 ml-2 align-middle cursor-help" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Product suspended</p>
                                  </TooltipContent>
                                </Tooltip>
                              ) : (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 ml-2 align-middle cursor-help" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Active</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {product.category?.name || '-'}
                            </span>
                          </div>
                        </div>
                      </TableCell>

                      {/* Brand Cell */}
                      <TableCell
                        className={`hidden md:table-cell ${selectedProduct ? 'md:hidden xl:table-cell' : ''}`}
                      >
                        {product.brand?.name ? (
                          <BrandHoverCard brand={product.brand} />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      {/* Strains Cell */}
                      <TableCell
                        className={`hidden md:table-cell ${selectedProduct ? 'md:hidden xl:table-cell' : ''}`}
                      >
                        {!product.strains || product.strains.length === 0 ? (
                          <div
                            className="inline-flex h-6 items-center px-2 cursor-pointer transition-all group/strains"
                            onClick={(e) => {
                              e.stopPropagation()
                              setEditingProduct(product)
                              setEditModalFocus('strain')
                              setIsEditModalOpen(true)
                            }}
                          >
                            <span className="text-muted-foreground group-hover/strains:hidden">
                              —
                            </span>
                            <span className="hidden group-hover/strains:inline-flex items-center text-xs font-medium text-muted-foreground hover:text-primary transition-colors whitespace-nowrap">
                              <Plus className="mr-1 h-3 w-3" /> Add Strain
                            </span>
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {product.strains?.slice(0, 2).map(({ strain }, idx) => (
                              <StrainHoverCard key={idx} strain={strain} />
                            ))}
                            {(product.strains?.length || 0) > 2 && (
                              <Badge
                                variant="secondary"
                                className="text-[10px] h-5 px-1.5 font-normal"
                              >
                                +{(product.strains?.length || 0) - 2}
                              </Badge>
                            )}
                          </div>
                        )}
                      </TableCell>

                      {/* Price Cell */}
                      <TableCell
                        className={`hidden lg:table-cell ${selectedProduct ? 'lg:hidden 2xl:table-cell' : ''}`}
                      >
                        {product.saleConfigs === undefined ? (
                          <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                        ) : (
                          <HoverCard>
                            <HoverCardTrigger asChild>
                              <div className="inline-flex cursor-help">
                                {product.saleConfigs.length === 0 ? (
                                  <div
                                    className="inline-flex h-6 items-center px-2 cursor-pointer transition-all group/pricing"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setEditingSaleConfigId(null)
                                      setSaleConfigProductId(product.id)
                                      setSaleConfigProduct(product)
                                      setSelectedProductId(product.slug || product.id)
                                      setIsAddSaleConfigOpen(true)
                                    }}
                                  >
                                    <span className="text-muted-foreground group-hover/pricing:hidden">
                                      —
                                    </span>
                                    <span className="hidden group-hover/pricing:inline-flex items-center text-xs font-medium text-muted-foreground hover:text-primary transition-colors whitespace-nowrap">
                                      <Plus className="mr-1 h-3 w-3" /> Add Config
                                    </span>
                                  </div>
                                ) : product.saleConfigs.length === 1 ? (
                                  (() => {
                                    const config = product.saleConfigs[0]
                                    const uom =
                                      config.sellUom?.shortForm ||
                                      (config.sellType === 'WEIGHT' ? 'g' : 'ea')

                                    const isTiered = !!config.pricingScheme
                                    const tiers = config.pricingScheme?.tiers || []
                                    const isExplicitlyTiered =
                                      config.pricingScheme?.mode === 'TIERED'

                                    let simplePriceCents: number | null | undefined =
                                      config.flatPriceCents ??
                                      config.simplePriceCents ??
                                      config.pricingScheme?.flatPriceCents

                                    if (
                                      (simplePriceCents === null ||
                                        simplePriceCents === undefined) &&
                                      isTiered &&
                                      tiers.length === 1
                                    ) {
                                      const singleTier = tiers[0]
                                      const startQty = Number(singleTier.startQuantity)
                                      if (!isNaN(startQty) && startQty <= 1.0001) {
                                        simplePriceCents = singleTier.unitPriceCents
                                      }
                                    }

                                    if (
                                      isExplicitlyTiered &&
                                      tiers.length > 0 &&
                                      (simplePriceCents === null ||
                                        simplePriceCents === undefined)
                                    ) {
                                      const minPrice = Math.min(
                                        ...tiers.map(
                                          (t: { unitPriceCents: number }) => t.unitPriceCents,
                                        ),
                                      )
                                      const formattedPrice = formatCurrency(minPrice)
                                      return (
                                        <Badge
                                          variant="secondary"
                                          className="font-mono text-xs"
                                        >
                                          From {formattedPrice}/{uom}
                                        </Badge>
                                      )
                                    } else {
                                      const finalPrice = simplePriceCents ?? 0
                                      const price = formatCurrency(finalPrice)
                                      return (
                                        <Badge
                                          variant="secondary"
                                          className="font-mono text-xs"
                                        >
                                          {price}/{uom}
                                        </Badge>
                                      )
                                    }
                                  })()
                                ) : (
                                  <Badge variant="secondary" className="font-mono text-xs">
                                    {product.saleConfigs.length} Prices
                                  </Badge>
                                )}
                              </div>
                            </HoverCardTrigger>
                            <HoverCardContent className="w-80 p-0" align="start">
                              <div className="p-3 max-h-[300px] overflow-y-auto">
                                <SaleConfigListCompact configs={product.saleConfigs} />
                              </div>
                            </HoverCardContent>
                          </HoverCard>
                        )}
                      </TableCell>

                      {/* Actions Cell */}
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              className="h-8 w-8 p-0 opacity-100 xl:opacity-0 xl:group-hover:opacity-100 transition-opacity"
                            >
                              <span className="sr-only">Open menu</span>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {/* Main row actions */}
                            <DropdownMenuItem
                              className="gap-1.5 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation()
                                setEditingProduct(product)
                                setEditModalFocus(null)
                                setIsEditModalOpen(true)
                              }}
                            >
                              <Pencil className="h-4 w-4" /> Edit
                            </DropdownMenuItem>
                            {product.isSuspended ? (
                              <DropdownMenuItem
                                className="gap-1.5 cursor-pointer"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setSuspendingProduct(product)
                                  setIsResumeDialogOpen(true)
                                }}
                              >
                                <PlayCircle className="h-4 w-4" /> Resume
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                className="gap-1.5 text-red-500 hover:text-red-600 focus:text-red-600 cursor-pointer"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setSuspendingProduct(product)
                                  setIsSuspendDialogOpen(true)
                                }}
                              >
                                <Ban className="h-4 w-4" /> Suspend
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              className="gap-1.5 text-destructive focus:text-destructive cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation()
                                setDeleteId(product.id)
                              }}
                            >
                              <Trash2 className="h-4 w-4" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            {/* Pagination inside the border container */}
            <DataTablePagination
              page={page}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={limit}
              onPageChange={(p: number) =>
                navigate({ to: '.', search: (prev) => ({ ...prev, page: p }), replace: true })
              }
              onLimitChange={(l: number) =>
                navigate({
                  to: '.',
                  search: (prev) => ({ ...prev, limit: l, page: 1 }),
                  replace: true,
                })
              }
              entityName="products"
              isLoading={loading}
            />
            </>
            )}
          </div>
        </div>

        {/* Side Panel */}
        {selectedProduct && (
          <React.Suspense fallback={null}>
            <ProductSidePanel
              open={!!selectedProduct}
              product={{
                ...selectedProduct,
                classification:
                  selectedProduct.classification ||
                  (selectedProduct.category?.classificationId &&
                  classificationsMap[selectedProduct.category.classificationId]
                    ? {
                        id: selectedProduct.category.classificationId,
                        name: classificationsMap[selectedProduct.category.classificationId],
                      }
                    : undefined),
              }}
              onClose={closeSelectedProductPanel}
              onAddSaleConfig={() => {
                setEditingSaleConfigId(null)
                setSaleConfigProductId(selectedProduct.id)
                setSaleConfigProduct(selectedProduct)
                setIsAddSaleConfigOpen(true)
              }}
              onEditSaleConfig={(config: SaleConfig) => {
                setEditingSaleConfigId(config.id)
                setSaleConfigProductId(selectedProduct.id)
                setSaleConfigProduct(selectedProduct)
                setIsAddSaleConfigOpen(true)
              }}
              onEdit={(product: Product) => {
                setEditingProduct(product)
                setEditModalFocus(null)
                setIsEditModalOpen(true)
              }}
              onDelete={(id: string) => setDeleteId(id)}
              onRefresh={() => {
                queryClient.invalidateQueries({ queryKey: queryKeys.products(shopId) })
                if (selectedProduct?.id) {
                  queryClient.invalidateQueries({ queryKey: queryKeys.productDetail(selectedProduct.id, shopId) })
                }
              }}
            />
          </React.Suspense>
        )}
      </div>

      {/* Sale Config Sheet */}
      <React.Suspense fallback={null}>
        <SaleConfigSheet
          open={isAddSaleConfigOpen}
          onOpenChange={(open) => {
            setIsAddSaleConfigOpen(open)
            if (!open) {
              setEditingSaleConfigId(null)
              setSaleConfigProductId(null)
              setSaleConfigProduct(null)
            }
          }}
          productId={activeSaleConfigProductId}
          categoryId={activeSaleConfigProduct?.categoryId}
          saleConfigId={editingSaleConfigId}
          shopId={shopId}
          shops={shops}
          productName={activeSaleConfigProduct?.name}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: queryKeys.products(shopId) })
            if (selectedProduct?.id) {
              queryClient.invalidateQueries({ queryKey: queryKeys.productDetail(selectedProduct.id, shopId) })
            }
          }}
        />
      </React.Suspense>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={!!deleteId}
        onOpenChange={(open: boolean) => !open && setDeleteId(null)}
        title="Delete Product?"
        description="This action cannot be undone. This will permanently delete the product and all its associated data from our servers."
        onConfirm={() => {
          if (deleteId) deleteMutation.mutate(deleteId)
        }}
        confirmLabel="Delete Product"
        isPending={deleteMutation.isPending}
      />

      {/* Suspension Dialogs */}
      <Dialog
        open={isSuspendDialogOpen}
        onOpenChange={(v) => {
          setIsSuspendDialogOpen(v)
          if (!v) {
            setSuspendReason('')
            setSuspendingProduct(null)
          }
        }}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Ban className="h-5 w-5" />
              Suspend Product
            </DialogTitle>
            <DialogDescription>
              This will temporarily stop all sales for this product across all shops and configurations.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label htmlFor="suspend-reason">Reason</Label>
              <Textarea
                id="suspend-reason"
                placeholder="Enter reason for suspension..."
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                className="min-h-[80px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsSuspendDialogOpen(false)
                setSuspendReason('')
                setSuspendingProduct(null)
              }}
              disabled={suspendMutation.isPending}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => suspendMutation.mutate()} disabled={suspendMutation.isPending}>
              {suspendMutation.isPending ? 'Suspending...' : 'Suspend Product'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isResumeDialogOpen}
        onOpenChange={(v) => {
          setIsResumeDialogOpen(v)
          if (!v) {
            setResumeNote('')
            setSuspendingProduct(null)
          }
        }}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-600">
              <PlayCircle className="h-5 w-5" />
              Resume Product
            </DialogTitle>
            <DialogDescription>
              This will lift the suspension and allow this product to be sold again.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label htmlFor="resume-note">Note</Label>
              <Textarea
                id="resume-note"
                placeholder="Note for resuming..."
                value={resumeNote}
                onChange={(e) => setResumeNote(e.target.value)}
                className="min-h-[80px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsResumeDialogOpen(false)
                setResumeNote('')
                setSuspendingProduct(null)
              }}
              disabled={resumeMutation.isPending}
            >
              Cancel
            </Button>
            <Button onClick={() => resumeMutation.mutate()} disabled={resumeMutation.isPending}>
              {resumeMutation.isPending ? 'Resuming...' : 'Resume Product'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Product Modal */}
      <React.Suspense fallback={null}>
        <AddProductModal
          open={isAddModalOpen}
          onOpenChange={setIsAddModalOpen}
          onSuccess={(product: Product) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.products(shopId) })
            toast('Product added successfully', {
              icon: <CheckCircle className="h-4 w-4 text-emerald-500" />,
            })
            if (product && product.id) {
              setEditingSaleConfigId(null)
              setSaleConfigProductId(product.id)
              setSaleConfigProduct(product)
              navigate({
                to: '/catalog/products/$selectedItem',
                params: { selectedItem: product.slug || product.id },
                search: (prev) => ({ ...prev, openSaleConfig: true, saleConfigProductId: product.id }),
              })
            }
          }}
          onError={(msg: string) => toast.error(msg)}
        />
      </React.Suspense>

      {/* Edit Product Modal */}
      <React.Suspense fallback={null}>
        <EditProductModal
          open={isEditModalOpen}
          onOpenChange={(open) => {
            setIsEditModalOpen(open)
            if (!open) setEditModalFocus(null)
          }}
          product={editingProduct}
          focusOnField={editModalFocus}
          onSuccess={(product: Product) => {
            // Invalidate the products list (table) with the correct shopId
            queryClient.invalidateQueries({ queryKey: queryKeys.products(shopId) })
            // Invalidate the sidebar detail query for this product
            queryClient.invalidateQueries({ queryKey: queryKeys.productDetail(product.slug || product.id, shopId) })
            queryClient.invalidateQueries({ queryKey: queryKeys.productDetail(product.id, shopId) })
            toast('Product updated successfully', {
              icon: <CheckCircle className="h-4 w-4 text-emerald-500" />,
            })
            if (selectedProductId === product.id || selectedProductId === product.slug) {
              setSelectedProductId(product.slug || product.id)
            }
          }}
        />
      </React.Suspense>
    </div>
  )
}
